import { useMemo, useState } from "react";

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { BellIcon, MoreHorizontal, Search } from "lucide-react";

import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Container } from "@/components/pieces/bootstrap";
import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import { Avatar } from "@/components/pieces/users/Avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Filter } from "@/components/ui/filter";
import { Input } from "@/components/ui/input";
import { RoleplayRank } from "@/graphql";

import { AssignmentResultDrawer } from "./AssignmentResultDrawer";
import { AlertModal } from "./modals/AlertModal";
import { DeleteModal } from "./modals/DeleteModal";
import { EditModal, EditSettings } from "./modals/EditModal";
import { SCENARIO_ASSIGNMENT_STATUS_STYLES } from "./utils";

// Types for our assignment data
interface ParticipantAssignment {
  id: string;
  scenario: string;
  participant: {
    id: string;
    name: string;
    avatar?: string;
  };
  dueDate?: string;
  status: "COMPLETED" | "PAST_DUE" | "NOT_STARTED";
  bestRank?: RoleplayRank | "NotStarted" | null;
  bestScore?: number;
  sessions?: number;
  completedDate?: string;
  createdAt?: string;
}

// Mock data for demonstration
const mockAssignments: ParticipantAssignment[] = [
  {
    id: "1",
    scenario: "Difficult Customer Conversations",
    participant: {
      id: "user1",
      name: "Elizabeth Johnson",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    },
    status: "COMPLETED",
    bestRank: RoleplayRank.Gold,
    bestScore: 94,
    sessions: 3,
    completedDate: "Oct 3, 2025",
    createdAt: "Oct 16, 2025",
  },
  {
    id: "2",
    scenario: "Difficult Customer Conversations",
    participant: {
      id: "user2",
      name: "Tricia Smith",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    },
    status: "COMPLETED",
    bestRank: RoleplayRank.Silver,
    bestScore: 91,
    sessions: 2,
    createdAt: "Oct 16, 2025",
  },
  {
    id: "3",
    scenario: "Difficult Customer Conversations",
    participant: {
      id: "user3",
      name: "Zack Ableman",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    },
    status: "COMPLETED",
    bestRank: RoleplayRank.Bronze,
    bestScore: 84,
    sessions: 1,
    createdAt: "Oct 16, 2025",
  },
  {
    id: "4",
    scenario: "New Employee Onboarding",
    participant: {
      id: "user2",
      name: "Tricia Smith",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    },
    dueDate: "Oct 10, 2025",
    status: "PAST_DUE",
    bestRank: RoleplayRank.Unranked,
    createdAt: "Oct 16, 2025",
  },
  {
    id: "5",
    scenario: "Conflict Resolution Training",
    participant: {
      id: "user3",
      name: "Zack Ableman",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    },
    status: "NOT_STARTED",
    bestRank: "NotStarted",
    createdAt: "Oct 16, 2025",
  },
];

const columnHelper = createColumnHelper<ParticipantAssignment>();

interface ScenarioAssignmentContentProps {
  pageLoading?: boolean;
  page: "scenarioDetail" | "roleplayDetail";
}

export function ScenarioAssignmentContent({
  pageLoading = false,
  page,
}: ScenarioAssignmentContentProps) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assignmentResultDrawerOpen, setAssignmentResultDrawerOpen] =
    useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<ParticipantAssignment | null>(null);
  const [assignments, setAssignments] =
    useState<ParticipantAssignment[]>(mockAssignments);

  // Calculate stats
  const stats = useMemo(() => {
    const notStarted = mockAssignments.filter(
      (a) => a.status === "NOT_STARTED",
    ).length;
    const pastDue = mockAssignments.filter(
      (a) => a.status === "PAST_DUE",
    ).length;
    const complete = mockAssignments.filter(
      (a) => a.status === "COMPLETED",
    ).length;

    return [
      { status: "NOT_STARTED", count: notStarted },
      { status: "PAST_DUE", count: pastDue },
      { status: "COMPLETED", count: complete },
    ];
  }, []);

  const handleAlertSave = (settings: { weeklyDigest: boolean }) => {
    console.log("Alert settings saved:", settings);
    // TODO: Implement alert settings save
  };

  const handleEditSave = (settings: EditSettings) => {
    console.log("Edit settings saved:", settings);
    // TODO: Implement edit settings save
  };

  const handleEditAssignment = (assignment: ParticipantAssignment) => {
    setSelectedAssignment(assignment);
    setEditModalOpen(true);
  };

  const handleAlertClick = () => {
    setAlertModalOpen(true);
  };

  const handleDeleteAssignment = (assignment: ParticipantAssignment) => {
    setSelectedAssignment(assignment);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedAssignment) {
      setAssignments((prev) =>
        prev.filter((a) => a.id !== selectedAssignment.id),
      );
      setSelectedAssignment(null);
    }
    setDeleteModalOpen(false);
  };

  const columns = useMemo(
    () => [
      ...(page === "roleplayDetail"
        ? [
            columnHelper.accessor("scenario", {
              id: "scenario",
              header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Scenario" />
              ),
              cell: ({ getValue }) => (
                <div
                  className="font-medium"
                  style={{
                    width: "250px",
                    whiteSpace: "normal",
                  }}
                >
                  {getValue()}
                </div>
              ),
            }),
          ]
        : []),

      columnHelper.accessor("participant", {
        id: "assignedTo",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Assigned To" />
        ),
        cell: ({ getValue }) => {
          const participant = getValue();
          return (
            <div className="flex items-center gap-2 min-w-[200px]">
              <Avatar
                user={{
                  name: participant.name,
                  photo: participant.avatar,
                }}
                size={24}
              />
              <span
                className="font-medium"
                style={{
                  whiteSpace: "normal",
                }}
                title={participant.name}
              >
                {participant.name}
              </span>
            </div>
          );
        },
      }),

      columnHelper.accessor("createdAt", {
        id: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Assigned" />
        ),
        cell: ({ getValue }) => {
          const date = getValue();
          return <div className="text-md">{date}</div>;
        },
      }),

      columnHelper.accessor("dueDate", {
        id: "due",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Due" />
        ),
        cell: ({ getValue, row }) => {
          const dueDate = getValue();
          const status = row.original.status;

          return (
            <div className="text-md">
              {status === "PAST_DUE" && dueDate ? (
                <span className="text-yellow-800 font-medium">{dueDate}</span>
              ) : dueDate ? (
                <span>{dueDate}</span>
              ) : (
                <span className="text-gray-400">–</span>
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor("status", {
        id: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ getValue }) => {
          const status = getValue();

          const config = SCENARIO_ASSIGNMENT_STATUS_STYLES[status];

          return (
            <div
              className="flex items-center px-2 py-1 rounded-sm gap-2 w-fit"
              style={{ backgroundColor: config.backgroundColor }}
            >
              {config.icon}
              <div style={{ color: config.textColor }}>{config.text}</div>
            </div>
          );
        },
      }),

      columnHelper.accessor("bestScore", {
        id: "bestScore",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Best Score" />
        ),
        cell: ({ getValue, row }) => {
          const score = getValue();
          const sessions = row.original.sessions;

          if (!score) {
            return <span className="text-gray-400">–</span>;
          }

          return (
            <div
              className="flex items-center gap-2 relative cursor-pointer"
              onClick={() => {
                setSelectedAssignment(row.original);
                setAssignmentResultDrawerOpen(true);
              }}
            >
              <div className="flex items-center gap-1">
                <div className="flex items-center">
                  <RankIcon rank={row.original.bestRank} size={24} />
                </div>
                <span className="font-medium">{score}%</span>
              </div>
              {sessions && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                  {sessions} sessions
                </div>
              )}
            </div>
          );
        },
      }),

      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-gray-200"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleEditAssignment(row.original)}
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDeleteAssignment(row.original)}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: assignments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: "includesString",
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  if (pageLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Container>
        <div className="space-y-6 p-8 bg-white mt-8 rounded-lg border border-gray-200">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <div className="text-lg font-semibold text-gray-900">
                  Direct Assignments
                </div>
                <div className="text-sm text-gray-600 bg-gray-100 rounded-sm w-6 h-6 flex items-center justify-center">
                  5
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Roleplays assigned directly to users outside of programs
              </p>
            </div>
            <Button
              intent="secondary"
              variant="outline"
              className="gap-2 bg-gray-100 border-gray-200"
              onClick={handleAlertClick}
            >
              <BellIcon className="h-4 w-4" />
              Alerts
            </Button>
          </div>

          {/* Status Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stats.map(({ status, count }) => {
              const config =
                SCENARIO_ASSIGNMENT_STATUS_STYLES[
                  status as keyof typeof SCENARIO_ASSIGNMENT_STATUS_STYLES
                ];
              return (
                <Card
                  className="p-0 border-none"
                  key={status}
                  style={{ backgroundColor: config.backgroundColor }}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-3">
                        {config.icon}
                        <span
                          className="text-base"
                          style={{ color: config.textColor }}
                        >
                          {config.text}
                        </span>
                      </div>
                      <div
                        className="text-3xl font-semibold"
                        style={{ color: config.textColor }}
                      >
                        {count}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col gap-6">
            <div className="flex items-center border border-gray-300 rounded-lg px-2 h-10 w-full md:w-1/2">
              <Search className="h-4" />
              <Input
                placeholder="Search assignments"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="max-w-s border-none shadow-none px-1"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <div className="hide-scroll-bar flex gap-2 overflow-x-auto">
                {page === "roleplayDetail" && (
                  <Filter
                    variant="single"
                    value=""
                    label="Scenario"
                    options={[]}
                    onChange={() => {}}
                  />
                )}
                {page === "scenarioDetail" && (
                  <Filter
                    variant="single"
                    value=""
                    label="Assigned By"
                    options={[]}
                    onChange={() => {}}
                  />
                )}
                <Filter
                  variant="single"
                  value=""
                  label="Assignees"
                  options={[]}
                  onChange={() => {}}
                />
                <Filter
                  variant="single"
                  value=""
                  label="Status"
                  options={[]}
                  onChange={() => {}}
                />
                {page === "roleplayDetail" && (
                  <Filter
                    variant="single"
                    value=""
                    label="Any Owner"
                    options={[]}
                    onChange={() => {}}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Data Table */}
          <DataTable
            table={table}
            totalRecords={mockAssignments.length}
            pagination={true}
            showSelectedRowCount={false}
          />
        </div>
      </Container>

      {/* Modals */}
      <AlertModal
        open={alertModalOpen}
        onOpenChange={setAlertModalOpen}
        onSave={handleAlertSave}
      />

      <EditModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        onSave={handleEditSave}
        initialData={
          selectedAssignment
            ? {
                setDueDate: !!selectedAssignment.dueDate,
                dueDate: selectedAssignment.dueDate || "Oct 2, 2025",
              }
            : undefined
        }
      />

      <DeleteModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        assignmentName={selectedAssignment?.scenario}
        participantName={selectedAssignment?.participant.name}
      />

      <AssignmentResultDrawer
        // assignmentId={selectedAssignment?.id ?? ""}
        open={assignmentResultDrawerOpen}
        onOpenChange={setAssignmentResultDrawerOpen}
      />
    </>
  );
}
